/**
 * PlayerDock — bottom-docked audio player bar.
 *
 * Wraps the existing AudioPlayer logic and adds:
 *   - Playback speed selector (0.75 / 1 / 1.25 / 1.5 / 2×)
 *   - Collapse chevron (dock shrinks to a slim bar)
 *
 * Renders only when `url` is provided (same gate as AudioPlayer today).
 *
 * Positioning contract: rendered as the LAST CHILD of the meeting page's
 * content column. position:sticky bottom:0 pins it to the visible bottom
 * of main.page — the app's real scroll container (global.css .app is
 * viewport-bound) — while the transcript scrolls underneath. Because the
 * dock is in-flow, it reserves its own space at the end of the column, so
 * the last paragraphs always clear it at full scroll (no padding needed).
 *
 * --sticky-header-h is kept in sync by MeetingHeader; this component does
 * not touch it.
 *
 * This component publishes its OWN measured height to --player-dock-h on
 * document.documentElement (via a ResizeObserver on its root), mirroring the
 * MeetingHeader → --sticky-header-h pattern. The rework selection dock reads
 * --player-dock-h to stack directly ABOVE this player (so the two bottom docks
 * never overlap). The var updates automatically when the player expands /
 * collapses (its height changes). Reset to 0 on unmount so other pages — and
 * meetings without audio — get the default-0 behaviour (dock at viewport
 * bottom).
 */

import { useCallback, useEffect, useRef, useState, type Ref } from "react";
import { AudioPlayer, AudioPlayerHandle } from "../../components/AudioPlayer";
import { IconButton } from "../../ui/IconButton";
import { t } from "../../i18n";

interface Props {
  url: string | null;
  recordedAt: string | null;
  onTimeUpdate: (ms: number) => void;
  playerRef: Ref<AudioPlayerHandle>;
  /** Whether auto-scroll (Following) is currently engaged. Defaults true. */
  following?: boolean;
  /** Called when the user clicks the Follow / Following toggle. */
  onFollowToggle?: () => void;
}

const PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 2] as const;

export function PlayerDock({ url, recordedAt, onTimeUpdate, playerRef, following = true, onFollowToggle }: Props) {
  const [collapsed, setCollapsed] = useState(false);
  // Playback speed lives HERE (not in AudioPlayer). AudioPlayer applies
  // it to wavesurfer via setPlaybackRate (prop, on change + on create).
  const [rate, setRate] = useState(1);

  // Publish this dock's measured height to --player-dock-h so the rework
  // selection dock can stack directly above it. Mirrors MeetingHeader's
  // stickyHeaderRef → --sticky-header-h. A callback ref + ResizeObserver
  // keeps the var current as the dock expands/collapses. Cleared on unmount
  // (node === null) so meetings without audio / other pages fall back to 0.
  const dockResizeObsRef = useRef<ResizeObserver | null>(null);
  const dockRef = useCallback((node: HTMLDivElement | null) => {
    dockResizeObsRef.current?.disconnect();
    dockResizeObsRef.current = null;
    if (!node) {
      document.documentElement.style.removeProperty("--player-dock-h");
      return;
    }
    if (typeof ResizeObserver === "undefined") return;
    const apply = () =>
      document.documentElement.style.setProperty(
        "--player-dock-h",
        `${node.offsetHeight}px`,
      );
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(node);
    dockResizeObsRef.current = ro;
  }, []);

  // Belt-and-suspenders: ensure the var is cleared if this component unmounts
  // while the node ref-cleanup is somehow skipped (React always calls the ref
  // cleanup, but the explicit unmount effect guarantees no stale value leaks).
  useEffect(() => {
    return () => {
      document.documentElement.style.removeProperty("--player-dock-h");
    };
  }, []);

  if (!url) return null;

  // AudioPlayer stays MOUNTED when collapsed so wavesurfer is never destroyed:
  // playback position, buffered audio, and the playerRef handle all survive.
  // The waveform container is CSS-hidden (height 0 / overflow hidden) while the
  // slim collapsed bar shows the expand control.
  return (
    <div
      ref={dockRef}
      data-testid="player-dock"
      style={{
        position: "sticky",
        // calc(-1 * var(--page-pad)) compensates main.page's padding so the
        // dock sits flush with the viewport bottom. Same token as the header's
        // top inset — the magic -24 now lives in --page-pad (tokens.css).
        bottom: "calc(-1 * var(--page-pad, 24px))",
        zIndex: 10,
        marginTop: 12,
        background: "var(--surface)",
        borderTop: "1px solid var(--border)",
      }}
    >
      {/* Collapsed slim bar — always visible, toggles expand */}
      {collapsed && (
        <div
          style={{
            height: 36,
            display: "flex",
            alignItems: "center",
            paddingInline: 12,
            gap: 8,
          }}
        >
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Audio player</span>
          <IconButton
            label="Expand audio player"
            name="chevron-up"
            size={14}
            onClick={() => setCollapsed(false)}
          />
        </div>
      )}

      {/* Full dock controls — hidden when collapsed but AudioPlayer stays mounted */}
      <div
        data-testid="player-dock-expanded"
        style={{
          overflow: "hidden",
          height: collapsed ? 0 : "auto",
          visibility: collapsed ? "hidden" : "visible",
          paddingInline: collapsed ? 0 : 16,
          paddingBlock: collapsed ? 0 : 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          {/* Playback speed */}
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-muted)" }}>
            Speed
            <select
              aria-label="Playback speed"
              value={String(rate)}
              onChange={(e) => setRate(Number(e.target.value))}
              style={{
                background: "var(--surface-2)",
                color: "var(--text)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                fontSize: 12,
                padding: "2px 6px",
              }}
            >
              {PLAYBACK_RATES.map((r) => (
                <option key={r} value={String(r)}>
                  {r}×
                </option>
              ))}
            </select>
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Follow toggle: shown only when audio is loaded (url truthy, enforced by early-return above).
                "Following" = auto-scroll active (muted, just informational).
                "Follow"    = disengaged, accent-colored to invite re-engagement. */}
            <button
              type="button"
              className={`follow-toggle-btn${following ? " is-following" : ""}`}
              aria-label={following ? t("player.following") : t("player.follow")}
              title={following
                ? "Auto-scrolling to the current paragraph — click to disengage"
                : "Click to re-center on the current paragraph and resume auto-scroll"}
              onClick={onFollowToggle}
            >
              {following ? t("player.following") : t("player.follow")}
            </button>
            <IconButton
              label="Collapse audio player"
              name="chevron-down"
              size={14}
              onClick={() => setCollapsed(true)}
            />
          </div>
        </div>
        <AudioPlayer
          ref={playerRef}
          url={url}
          recordedAt={recordedAt}
          onTimeUpdate={onTimeUpdate}
          playbackRate={rate}
        />
      </div>
    </div>
  );
}
